import axios from 'axios';

const BASE_URL = import.meta.env.VITE_TB_BASE_URL || 'https://iot.seqsys.net';
const USERNAME = import.meta.env.VITE_TB_USERNAME || '';
const PASSWORD = import.meta.env.VITE_TB_PASSWORD || '';

export const tbClient = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Interceptor to handle 401 Unauthorized (Token Expiration)
tbClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // If error is 401 and we haven't retried yet
    // Skip if the request was to /api/auth/token or /api/auth/login to avoid infinite loops
    const isAuthRequest = originalRequest.url?.includes('/api/auth/login') || originalRequest.url?.includes('/api/auth/token');
    
    if (error.response && error.response.status === 401 && !originalRequest._retry && !isAuthRequest) {
      originalRequest._retry = true;
      try {
        console.log('Access token expired. Attempting to refresh...');
        // 1. Try refreshing using the refresh token
        let newToken = await thingsboardService.refreshJwtToken();
        
        // 2. If refresh failed, fall back to environment variables login (if provided)
        if (!newToken && (USERNAME || PASSWORD)) {
          console.log('Refresh token failed. Attempting login with env credentials...');
          newToken = await thingsboardService.login(undefined, undefined, true);
        }
        
        if (newToken) {
          // Update the failed request's header and retry
          originalRequest.headers['X-Authorization'] = `Bearer ${newToken}`;
          return tbClient(originalRequest);
        } else {
          // If both failed, notify the app to logout
          console.error('All authentication methods failed. Logging out...');
          window.dispatchEvent(new CustomEvent('tb-auth-failed'));
        }
      } catch (loginError) {
        window.dispatchEvent(new CustomEvent('tb-auth-failed'));
        return Promise.reject(loginError);
      }
    }
    return Promise.reject(error);
  }
);

let jwtToken: string | null = localStorage.getItem('jwtToken');
let refreshToken: string | null = localStorage.getItem('refreshToken');

export const thingsboardService = {
  /**
   * Login using environment variables
   */
  async login(username?: string, password?: string, force: boolean = false) {
    if (jwtToken && !force) return jwtToken;

    try {
      const loginUsername = username || USERNAME;
      const loginPassword = password || PASSWORD;

      if (!loginUsername || !loginPassword) {
         console.warn("TB credentials are not provided for fallback login");
         return null;
      }
      const response = await tbClient.post('/api/auth/login', {
        username: loginUsername,
        password: loginPassword,
      });
      jwtToken = response.data.token;
      refreshToken = response.data.refreshToken;
      if (jwtToken) localStorage.setItem('jwtToken', jwtToken);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      // Clean up previous user's space selection to prevent cross-account permission pollution
      localStorage.removeItem('apartmentAssetId');
      localStorage.removeItem('apartmentLabel');
      return jwtToken;
    } catch (error) {
      console.error('Failed to login to ThingsBoard', error);
      throw error;
    }
  },

  /**
   * Refresh JWT token using Refresh Token
   */
  async refreshJwtToken() {
    if (!refreshToken) {
      console.warn('No refresh token available');
      return null;
    }

    try {
      // Direct axios call instead of tbClient to avoid interceptor recursion if it somehow happened
      const response = await axios.post(`${BASE_URL}/api/auth/token`, {
        refreshToken: refreshToken
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      jwtToken = response.data.token;
      refreshToken = response.data.refreshToken;
      
      if (jwtToken) localStorage.setItem('jwtToken', jwtToken);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);
      
      console.log('Successfully refreshed ThingsBoard token');
      return jwtToken;
    } catch (error) {
      console.error('Failed to refresh ThingsBoard token', error);
      return null;
    }
  },

  async logout() {
    jwtToken = null;
    refreshToken = null;
    localStorage.removeItem('jwtToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('apartmentAssetId');
    localStorage.removeItem('apartmentLabel');
  },

  async getUser() {
    if (!jwtToken) await this.login();
    const response = await tbClient.get('/api/auth/user', {
      headers: { 'X-Authorization': `Bearer ${jwtToken}` }
    });
    return response.data;
  },

  async getAssetsByType(type: string, pageSize: number = 100, page: number = 0) {
    if (!jwtToken) await this.login();
    const response = await tbClient.get(`/api/tenant/assets?type=${type}&pageSize=${pageSize}&page=${page}`, {
      headers: { 'X-Authorization': `Bearer ${jwtToken}` }
    });
    return response.data;
  },

    async getUserAssets(pageSize: number = 100, page: number = 0) {
    if (!jwtToken) return { data: [] };
    try {
      const response = await tbClient.get(`/api/user/assets?pageSize=${pageSize}&page=${page}`, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });
      return response.data || { data: [] };
    } catch (e) {
      console.warn('Failed to call /api/user/assets:', e);
      return { data: [] };
    }
  },

  async getCustomerAssets(customerId: string, pageSize: number = 100, page: number = 0) {
    if (!jwtToken) return { data: [] };
    // Try user assets first
    const uRes = await this.getUserAssets(pageSize, page);
    if (uRes && uRes.data && uRes.data.length > 0) {
      return uRes;
    }
    try {
      const response = await tbClient.get(`/api/customer/${customerId}/assets?pageSize=${pageSize}&page=${page}`, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });
      return response.data || { data: [] };
    } catch (e) {
      console.error('Failed to get customer assets:', e);
      return { data: [] };
    }
  },

  async getCustomerAssetsByType(customerId: string, type: string, pageSize: number = 100, page: number = 0) {
    const all = await this.getCustomerAssets(customerId, pageSize, page);
    const filtered = (all?.data || []).filter((a: any) => a.type === type);
    return { data: filtered };
  },

  getJwtToken() {
    return jwtToken;
  },

  getWsUrl() {
    return `${BASE_URL.replace(/^http/, 'ws')}/api/ws/plugins/telemetry?token=${jwtToken}`;
    //return `${BASE_URL.replace(/^http/, 'ws')}/api/ws?token=${jwtToken}`;
  },

  getAlarmWsUrl() {
    return `${BASE_URL.replace(/^http/, 'ws')}/api/ws?token=${jwtToken}`;
    //return `${BASE_URL.replace(/^http/, 'ws')}/api/ws?token=${jwtToken}`;
  },



  /**
   * Get historical timeseries data
   */
  async getTimeseries(
    entityType: 'DEVICE' | 'ASSET',
    entityId: string,
    keys: string,
    startTs: number,
    endTs: number,
    interval: number = 0,
    limit: number = 100,
    agg: 'NONE' | 'MIN' | 'MAX' | 'AVG' | 'SUM' | 'COUNT' = 'NONE'
  ) {
    if (!entityId || !entityId.trim()) return {};
    if (!jwtToken) jwtToken = localStorage.getItem('jwtToken');
    if (!jwtToken) await this.login();
    if (!jwtToken) return {};

    const params = new URLSearchParams({
      keys,
      startTs: startTs.toString(),
      endTs: endTs.toString(),
      interval: interval.toString(),
      limit: limit.toString(),
      agg
    });

    const response = await tbClient.get(
      `/api/plugins/telemetry/${entityType}/${entityId}/values/timeseries?${params.toString()}`,
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Get latest telemetry values
   */
  async getLatestTelemetry(entityType: 'DEVICE' | 'ASSET', entityId: string, keys: string | string[]) {
    if (!entityId || !entityId.trim()) return {};
    if (!jwtToken) jwtToken = localStorage.getItem('jwtToken');
    if (!jwtToken) await this.login();
    if (!jwtToken) return {};

    const keysParam = Array.isArray(keys) ? keys.join(',') : keys;
    const response = await tbClient.get(
      `/api/plugins/telemetry/${entityType}/${entityId}/values/timeseries?keys=${keysParam}&useStrictDataTypes=true`,
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Get attributes for a device
   */
  async getAttributes(entityType: 'DEVICE' | 'ASSET', entityId: string, keys?: string, scope: string = 'SERVER_SCOPE') {
    if (!jwtToken) await this.login();
    const keysParam = keys ? `keys=${keys}` : '';
    const response = await tbClient.get(
      `/api/plugins/telemetry/${entityType}/${entityId}/values/attributes/${scope}?${keysParam}`,
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Get target relations (e.g. Zone assets contained in an Apartment asset)
   */
  async getRelations(entityType: 'DEVICE' | 'ASSET', entityId: string, relationTypeGroup: string = 'COMMON') {
    if (!jwtToken) await this.login();
    const response = await tbClient.get(
      `/api/relations/info?fromId=${entityId}&fromType=${entityType}&relationTypeGroup=${relationTypeGroup}`,
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Get attributes for an entity
   */
  async getEntityAttributes(entityType: 'DEVICE' | 'ASSET' | 'TENANT' | 'CUSTOMER', entityId: string, scope: string, keys?: string[]) {
    if (!jwtToken) await this.login();
    const url = keys 
      ? `/api/plugins/telemetry/${entityType}/${entityId}/values/attributes/${scope}?keys=${keys.join(',')}`
      : `/api/plugins/telemetry/${entityType}/${entityId}/values/attributes/${scope}`;
    
    const response = await tbClient.get(url, { headers: { 'X-Authorization': `Bearer ${jwtToken}` } });
    return response.data;
  },

  /**
   * Save attributes for an entity
   */
  async saveEntityAttributes(entityType: 'DEVICE' | 'ASSET' | 'TENANT' | 'CUSTOMER', entityId: string, scope: string, payload: Record<string, any>) {
    if (!jwtToken) await this.login();
    const response = await tbClient.post(
      `/api/plugins/telemetry/${entityType}/${entityId}/attributes/${scope}`,
      payload,
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Create a new Asset
   */
  async createAsset(name: string, type: string) {
    if (!jwtToken) await this.login();
    const response = await tbClient.post(
      `/api/asset`,
      { name, type },
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Delete an Asset
   */
  async deleteAsset(assetId: string) {
    if (!jwtToken) await this.login();
    const response = await tbClient.delete(
      `/api/asset/${assetId}`,
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Get an Asset by ID
   */
  async getAsset(assetId: string) {
    if (!jwtToken) await this.login();
    const response = await tbClient.get(
      `/api/asset/${assetId}`,
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Get a Device by ID
   */
  async getDevice(deviceId: string) {
    if (!jwtToken) await this.login();
    const response = await tbClient.get(
      `/api/device/${deviceId}`,
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Update a Device's label property and attributes in ThingsBoard
   */
  async updateDeviceLabel(deviceId: string | any, newLabel: string, applianceNames: string[] = []) {
    if (!jwtToken) await this.login();
    const rawId = typeof deviceId === 'object' && deviceId?.id ? deviceId.id : String(deviceId);
    let updatedSuccess = false;
    let forbiddenError: any = null;

    // 1. Direct update to Device Entity (/api/device)
    try {
      const dev = await this.getDevice(rawId);
      if (dev) {
        dev.label = newLabel;
        await tbClient.post('/api/device', dev, {
          headers: { 'X-Authorization': `Bearer ${jwtToken}` }
        });
        updatedSuccess = true;
        console.log(`Successfully updated Device ${rawId} label to ${newLabel}`);
      }
    } catch (err: any) {
      console.warn('Direct device label update via /api/device failed:', err);
      if (err?.response?.status === 403 || err?.status === 403) {
        forbiddenError = err;
      }
    }

    // 2. Also save attributes in SERVER_SCOPE and SHARED_SCOPE
    try {
      const attrs = {
        label: newLabel,
        primary_appliance: newLabel,
        appliance_names: applianceNames.length > 0 ? JSON.stringify(applianceNames) : (newLabel ? JSON.stringify([newLabel]) : '')
      };
      await this.saveEntityAttributes('DEVICE', rawId, 'SERVER_SCOPE', attrs).catch((e) => {
        if (e?.response?.status === 403) forbiddenError = e;
      });
      await this.saveEntityAttributes('DEVICE', rawId, 'SHARED_SCOPE', attrs).catch((e) => {
        if (e?.response?.status === 403) forbiddenError = e;
      });
      updatedSuccess = true;
    } catch (err: any) {
      console.warn('Failed to save device label attributes:', err);
      if (err?.response?.status === 403 || err?.status === 403) {
        forbiddenError = err;
      }
    }

    if (!updatedSuccess && forbiddenError) {
      const permErr: any = new Error('PERMISSION_DENIED');
      permErr.status = 403;
      permErr.response = forbiddenError.response;
      throw permErr;
    }
  },

  /**
   * Save or update a full Device object
   */
  async saveDevice(device: any) {
    if (!jwtToken) await this.login();
    const response = await tbClient.post(
      '/api/device',
      device,
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Get a Device by Name (Tenant Scope)
   */
  async getTenantDevice(deviceName: string) {
    if (!jwtToken) await this.login();
    const response = await tbClient.get(
      `/api/tenant/devices?deviceName=${deviceName}`,
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Create a new Device
   */
  async createDevice(name: string, type: string, label: string) {
    if (!jwtToken) await this.login();
    const response = await tbClient.post(
      `/api/device`,
      { name, type, label },
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Create a new relation
   */
  async createRelation(
    fromId: string,
    fromType: 'ASSET' | 'DEVICE',
    toId: string,
    toType: 'ASSET' | 'DEVICE',
    relationType: string = 'Contains',
    relationTypeGroup: string = 'COMMON'
  ) {
    if (!jwtToken) await this.login();
    const response = await tbClient.post(
      `/api/relation`,
      {
        from: { id: fromId, entityType: fromType },
        to: { id: toId, entityType: toType },
        type: relationType,
        typeGroup: relationTypeGroup
      },
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Find relations targeting this entity (e.g. SES --Control--> SEV)
   */
  async findRelationsByTo(entityType: 'DEVICE' | 'ASSET', entityId: string, relationTypeGroup: string = 'COMMON') {
    if (!jwtToken) await this.login();
    const response = await tbClient.get(
      `/api/relations/info?toId=${entityId}&toType=${entityType}&relationTypeGroup=${relationTypeGroup}`,
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Find relations originating from this entity (e.g. SEV --Control--> SES)
   */
  async findRelationsByFrom(entityType: 'DEVICE' | 'ASSET', entityId: string, relationTypeGroup: string = 'COMMON') {
    if (!jwtToken) await this.login();
    const response = await tbClient.get(
      `/api/relations/info?fromId=${entityId}&fromType=${entityType}&relationTypeGroup=${relationTypeGroup}`,
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Delete a relation
   */
  async deleteRelation(
    fromId: string,
    fromType: 'ASSET' | 'DEVICE',
    toId: string,
    toType: 'ASSET' | 'DEVICE',
    relationType: string = 'Control',
    relationTypeGroup: string = 'COMMON'
  ) {
    if (!jwtToken) await this.login();
    const response = await tbClient.delete(
      `/api/relation?fromId=${fromId}&fromType=${fromType}&relationType=${relationType}&relationTypeGroup=${relationTypeGroup}&toId=${toId}&toType=${toType}`,
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Get zone SES devices for a given device ID
   */
  async getZoneSesDevices(deviceId: string) {
    if (!jwtToken) await this.login();
    try {
      // 1. Get parent relations of this device (to find Zone Asset)
      const parentRels = await tbClient.get(
        `/api/relations/info?toId=${deviceId}&toType=DEVICE&relationTypeGroup=COMMON`,
        { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
      );
      const zoneRel = parentRels.data?.find((r: any) => r.from?.entityType === 'ASSET');
      if (!zoneRel) return { zoneName: '', zoneId: '', sesDevices: [] };

      // 2. Get all devices in this Zone Asset
      const zoneDevices = await tbClient.get(
        `/api/relations/info?fromId=${zoneRel.from.id}&fromType=ASSET&relationTypeGroup=COMMON`,
        { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
      );

      const sesDevices = (zoneDevices.data || [])
        .filter((r: any) => r.to?.entityType === 'DEVICE' && (r.toName?.startsWith('SES') || r.type === 'SES'))
        .map((r: any) => ({ id: r.to.id, name: r.toName || 'SES Switch' }));

      return {
        zoneName: zoneRel.fromName || '',
        zoneId: zoneRel.from.id,
        sesDevices
      };
    } catch (error) {
      console.error('Failed to get zone SES devices:', error);
      return { zoneName: '', zoneId: '', sesDevices: [] };
    }
  },

  /**
   * Send One-Way RPC request to a device
   */
  async sendOneWayRpc(deviceId: string, method: string, params: any) {
    if (!jwtToken) await this.login();
    const response = await tbClient.post(
      `/api/rpc/oneway/${deviceId}`,
      {
        method: method,
        params: params
      },
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Get Alarms for the current user
   */
  async getAlarms(pageSize: number = 20, page: number = 0, searchStatus: 'ANY' | 'ACTIVE' | 'CLEARED' | 'ACK' | 'UNACK' = 'ANY') {
    if (!jwtToken) await this.login();
    const params = new URLSearchParams({
      pageSize: pageSize.toString(),
      page: page.toString(),
      searchStatus
    });
    const response = await tbClient.get(`/api/alarms?${params.toString()}`, {
      headers: { 'X-Authorization': `Bearer ${jwtToken}` }
    });
    return response.data;
  },

  /**
   * Get Alarms for a specific customer
   */
  async getCustomerAlarms(customerId: string, pageSize: number = 20, page: number = 0, searchStatus: 'ANY' | 'ACTIVE' | 'CLEARED' | 'ACK' | 'UNACK' = 'ANY') {
    if (!jwtToken) await this.login();
    const params = new URLSearchParams({
      pageSize: pageSize.toString(),
      page: page.toString(),
      searchStatus
    });
    const response = await tbClient.get(`/api/alarm/CUSTOMER/${customerId}?${params.toString()}`, {
      headers: { 'X-Authorization': `Bearer ${jwtToken}` }
    });
    return response.data;
  },

  /**
   * Acknowledge an alarm
   */
  async acknowledgeAlarm(alarmId: string) {
    if (!jwtToken) await this.login();
    const response = await tbClient.post(
      `/api/alarm/${alarmId}/ack`,
      {},
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * Clear an alarm
   */
  async clearAlarm(alarmId: string) {
    if (!jwtToken) await this.login();
    const response = await tbClient.post(
      `/api/alarm/${alarmId}/clear`,
      {},
      { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
    );
    return response.data;
  },

  /**
   * ─────────────────────────────────────────────────────────────
   * ThingsBoard Professional Edition (PE) Scheduler Event APIs
   * ─────────────────────────────────────────────────────────────
   */
  async getSchedulerEvents(pageSize: number = 100, page: number = 0, sortProperty: string = 'createdTime', sortOrder: 'ASC' | 'DESC' = 'DESC') {
    if (!jwtToken) await this.login();
    const params = new URLSearchParams({
      pageSize: pageSize.toString(),
      page: page.toString(),
      sortProperty,
      sortOrder
    });
    try {
      const response = await tbClient.get(`/api/schedulerEvents?${params.toString()}`, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });
      return response.data;
    } catch (e: any) {
      // Fallback in case of path variations
      const response = await tbClient.get(`/api/scheduler/events?${params.toString()}`, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });
      return response.data;
    }
  },

  async getSchedulerEvent(id: string) {
    if (!jwtToken) await this.login();
    try {
      const response = await tbClient.get(`/api/schedulerEvent/${id}`, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });
      return response.data;
    } catch (e) {
      const response = await tbClient.get(`/api/scheduler/event/${id}`, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });
      return response.data;
    }
  },

  async saveSchedulerEvent(schedulerEvent: any) {
    if (!jwtToken) await this.login();
    try {
      const response = await tbClient.post(
        `/api/schedulerEvent`,
        schedulerEvent,
        { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
      );
      return response.data;
    } catch (e: any) {
      if (e?.response?.status === 404) {
        // Retry with alternate endpoint
        const response = await tbClient.post(
          `/api/scheduler/event`,
          schedulerEvent,
          { headers: { 'X-Authorization': `Bearer ${jwtToken}` } }
        );
        return response.data;
      }
      throw e;
    }
  },

  async deleteSchedulerEvent(id: string) {
    if (!jwtToken) await this.login();
    try {
      const response = await tbClient.delete(`/api/schedulerEvent/${id}`, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });
      return response.data;
    } catch (e) {
      const response = await tbClient.delete(`/api/scheduler/event/${id}`, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });
      return response.data;
    }
  },

  /**
   * Get all customers for registration dropdown
   */
  
  /**
   * Get single customer by ID
   */
  async getCustomerById(customerId: string) {
    try {
      if (!jwtToken) await this.login();
      const response = await tbClient.get(`/api/customer/${customerId}`, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });
      return response.data;
    } catch (error) {
      console.warn('Failed to fetch customer by ID:', customerId);
      return null;
    }
  },

  async getCustomersList(pageSize: number = 100, page: number = 0) {
    try {
      if (!jwtToken) await this.login();
      const response = await tbClient.get(`/api/tenant/customers?pageSize=${pageSize}&page=${page}`, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });
      return response.data.data || [];
    } catch (error) {
      console.error('Failed to fetch customers list', error);
      return [];
    }
  },

  /**
   * Get Server-Scope attributes for a user
   */
  async getUserServerAttributes(userId: string) {
    try {
      if (!jwtToken) await this.login();
      const response = await tbClient.get(`/api/plugins/telemetry/USER/${userId}/values/attributes/SERVER_SCOPE`, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });
      const attrs: Record<string, any> = {};
      if (Array.isArray(response.data)) {
        response.data.forEach((item: any) => {
          attrs[item.key] = item.value;
        });
      }
      return attrs;
    } catch (error) {
      console.error('Failed to get user server attributes', error);
      return {};
    }
  },

  /**
   * Set Server-Scope attributes for a user
   */
  async setUserServerAttributes(userId: string, attributes: Record<string, any>) {
    try {
      if (!jwtToken) await this.login();
      await tbClient.post(`/api/plugins/telemetry/USER/${userId}/SERVER_SCOPE`, attributes, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });
      return true;
    } catch (error) {
      console.error('Failed to set user server attributes', error);
      throw error;
    }
  },

  /**
   * Get all users under a customer with their status and attributes
   */
  async getCustomerUsersWithAttributes(customerId: string, pageSize: number = 100, page: number = 0) {
    try {
      if (!jwtToken) await this.login();
      let users: any[] = [];
      if (customerId) {
        const response = await tbClient.get(`/api/customer/${customerId}/users?pageSize=${pageSize}&page=${page}`, {
          headers: { 'X-Authorization': `Bearer ${jwtToken}` }
        });
        users = response.data.data || [];
      } else {
        // Fallback or tenant admin fetching
        const response = await tbClient.get(`/api/users?pageSize=${pageSize}&page=${page}`, {
          headers: { 'X-Authorization': `Bearer ${jwtToken}` }
        });
        users = response.data.data || [];
      }

      // Fetch server attributes for each user
      const usersWithAttrs = await Promise.all(
        users.map(async (u) => {
          const attrs = await this.getUserServerAttributes(u.id.id);
          const addInfo = u.additionalInfo || {};
          
          // Determine status: If explicitly marked PENDING_APPROVAL or not active/no approval record, it is PENDING
          let status = attrs.status || addInfo.status;
          if (!status) {
            if (addInfo.userActivated === false || addInfo.userCredentialsEnabled === false || u.authority === 'CUSTOMER_USER') {
              status = 'PENDING_APPROVAL';
            } else {
              status = 'ACTIVE';
            }
          }

          let role = attrs.userRole || addInfo.userRole || 'ZONE_USER';
          let allowed = attrs.allowedZoneIds || addInfo.allowedZoneIds || [];
          if (typeof allowed === 'string') {
            try { allowed = JSON.parse(allowed); } catch { allowed = []; }
          }

          return {
            ...u,
            serverAttributes: attrs,
            status: status,
            userRole: role,
            allowedZoneIds: Array.isArray(allowed) ? allowed : [],
            canControl: attrs.canControl !== undefined ? attrs.canControl : (addInfo.canControl !== undefined ? addInfo.canControl : true),
          };
        })
      );

      return usersWithAttrs;
    } catch (error) {
      console.error('Failed to fetch customer users with attributes', error);
      return [];
    }
  },

    /**
   * Register a new customer user (Signs up as PENDING_APPROVAL)
   */
  async registerCustomerUser(data: {
    email: string;
    password?: string;
    firstName: string;
    lastName?: string;
    customerId: string;
    customerTitle?: string;
    phoneNumber?: string;
  }) {
    try {
      if (!jwtToken) await this.login();

      // 1. Create User in ThingsBoard with PENDING_APPROVAL
      const createRes = await tbClient.post('/api/user?sendActivationMail=false', {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName || '',
        customerId: { id: data.customerId, entityType: 'CUSTOMER' },
        authority: 'CUSTOMER_USER',
        additionalInfo: {
          customerTitle: data.customerTitle || '',
          phoneNumber: data.phoneNumber || '',
          status: 'PENDING_APPROVAL',
          userRole: 'ZONE_USER',
          allowedZoneIds: [],
          canControl: true,
          defaultDashboardId: '2c094660-2e76-11f1-81ed-0bafda459615',
          defaultDashboardFullscreen: true,
          homeDashboardId: '2c094660-2e76-11f1-81ed-0bafda459615',
          homeDashboardHideToolbar: true,
          userCredentialsEnabled: true,
          userActivated: false,
          registeredAt: Date.now()
        }
      }, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });

      const newUser = createRes.data;
      const userId = newUser.id.id;

      // 2. Set password directly in ThingsBoard credentials table via activateToken
      if (data.password) {
        try {
          const actRes = await tbClient.get(`/api/user/${userId}/activationLink`, {
            headers: {
              'X-Authorization': `Bearer ${jwtToken}`,
              'Accept': 'text/plain, */*'
            },
            responseType: 'text'
          });
          const rawLink = typeof actRes.data === 'string' ? actRes.data : JSON.stringify(actRes.data);
          const tokenMatch = rawLink.match(/activateToken=([^&"'\s]+)/);
          const activateToken = tokenMatch ? tokenMatch[1] : null;
          if (activateToken) {
            await tbClient.post('/api/noauth/activate', {
              activateToken: activateToken,
              password: data.password
            });
            console.log('Successfully initialized credentials for new pending user:', userId);
          }
        } catch (pwErr) {
          console.warn('Initial credentials activation attempt:', pwErr);
        }
      }

      // 3. Mark User Server Attributes as PENDING_APPROVAL
      await this.setUserServerAttributes(userId, {
        status: 'PENDING_APPROVAL',
        userRole: 'ZONE_USER',
        allowedZoneIds: JSON.stringify([]),
        canControl: true,
        customerTitle: data.customerTitle || '',
        registeredAt: Date.now()
      });

      return newUser;
    } catch (error: any) {
      console.error('Failed to register customer user', error);
      throw new Error(error.response?.data?.message || '회원가입 요청 중 오류가 발생했습니다.');
    }
  },

  /**
   * Approve a pending customer user and assign Zone permissions
   */
  async approveCustomerUser(userId: string, options: {
    allowedZoneIds: string[];
    userRole: 'CUSTOMER_ADMIN' | 'ZONE_USER';
    canControl: boolean;
  }) {
    try {
      if (!jwtToken) await this.login();
      
      // 1. Fetch user to preserve other info
      const userRes = await tbClient.get(`/api/user/${userId}`, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });
      const user = userRes.data;

      // 2. If user credentials still have an unconsumed activation token, activate it
      try {
        const actRes = await tbClient.get(`/api/user/${userId}/activationLink`, {
          headers: {
            'X-Authorization': `Bearer ${jwtToken}`,
            'Accept': 'text/plain, */*'
          },
          responseType: 'text'
        });
        const rawLink = typeof actRes.data === 'string' ? actRes.data : JSON.stringify(actRes.data);
        const tokenMatch = rawLink.match(/activateToken=([^&"'\s]+)/);
        const activateToken = tokenMatch ? tokenMatch[1] : null;
        if (activateToken) {
          await tbClient.post('/api/noauth/activate', {
            activateToken: activateToken,
            password: 'tkatjdanftks123!'
          });
          console.log(`Consumed remaining activationToken for user ${userId}`);
        }
      } catch (actErr) {
        // Already activated or credentials ready
      }

      // 3. Automatically add to Customer Users / Customer Administrators Entity Group in ThingsBoard PE
      const customerId = user.customerId?.id;
      if (customerId) {
        try {
          const egRes = await tbClient.get(`/api/entityGroups/CUSTOMER/${customerId}/USER?pageSize=100&page=0`, {
            headers: { 'X-Authorization': `Bearer ${jwtToken}` }
          });
          const groups = egRes.data?.data || [];
          const targetGroupName = options.userRole === 'CUSTOMER_ADMIN' ? 'Customer Administrators' : 'Customer Users';
          const targetGroup = groups.find((g: any) => g.name === targetGroupName) || groups.find((g: any) => g.name === 'Customer Users') || groups[0];
          if (targetGroup) {
            await tbClient.post(`/api/entityGroup/${targetGroup.id.id}/addEntities`, [userId], {
              headers: { 'X-Authorization': `Bearer ${jwtToken}` }
            });
            console.log(`Successfully added user ${userId} to ThingsBoard PE group ${targetGroup.name}`);
          }
        } catch (groupErr) {
          console.warn('Failed to auto-add to PE user group:', groupErr);
        }
      }

      // 4. Update User additionalInfo & activate
      user.additionalInfo = {
        ...(user.additionalInfo || {}),
        status: 'ACTIVE',
        userRole: options.userRole,
        allowedZoneIds: options.allowedZoneIds,
        canControl: options.canControl,
        defaultDashboardId: '2c094660-2e76-11f1-81ed-0bafda459615',
        defaultDashboardFullscreen: true,
        homeDashboardId: '2c094660-2e76-11f1-81ed-0bafda459615',
        homeDashboardHideToolbar: true,
        userCredentialsEnabled: true,
        userActivated: true,
        approvedAt: Date.now()
      };

      await tbClient.post('/api/user', user, {
        headers: { 'X-Authorization': `Bearer ${jwtToken}` }
      });

      // 5. Save to server-scope attributes
      await this.setUserServerAttributes(userId, {
        status: 'ACTIVE',
        userRole: options.userRole,
        allowedZoneIds: JSON.stringify(options.allowedZoneIds),
        canControl: options.canControl,
        approvedAt: Date.now()
      });
      return true;
    } catch (error) {
      console.error('Failed to approve user', error);
      throw error;
    }
  },


  /**
   * Reject a user registration
   */
  async rejectCustomerUser(userId: string, reason?: string) {
    try {
      await this.setUserServerAttributes(userId, {
        status: 'REJECTED',
        rejectedAt: Date.now(),
        rejectReason: reason || '관리자에 의해 승인이 거절되었습니다.'
      });
      return true;
    } catch (error) {
      console.error('Failed to reject user', error);
      throw error;
    }
  },

  /**
   * Update existing user permissions
   */
  async updateUserPermissions(userId: string, options: {
    allowedZoneIds: string[];
    userRole: 'CUSTOMER_ADMIN' | 'ZONE_USER';
    canControl: boolean;
    status?: 'ACTIVE' | 'PENDING_APPROVAL' | 'REJECTED';
  }) {
    try {
      const updateData: Record<string, any> = {
        userRole: options.userRole,
        allowedZoneIds: JSON.stringify(options.allowedZoneIds),
        canControl: options.canControl,
      };
      if (options.status) {
        updateData.status = options.status;
      }
      await this.setUserServerAttributes(userId, updateData);
      return true;
    } catch (error) {
      console.error('Failed to update user permissions', error);
      throw error;
    }
  },

};


